import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Link } from 'react-router-dom';
import { Check, Minus, Smartphone, Share2, MoreVertical } from 'lucide-react';
import SEO from '@/components/SEO';

// Quick-comparison rows. Keep this short — full feature list lives on the
// About + Pricing pages. The "—" sentinel renders an em-dash for tiers
// where the feature is N/A (e.g. instructor-only perks on player rows).
const PLAYER_TIER_ROWS = [
  ['Create & edit your profile', true, true],
  ['View events & instructor contacts', true, true],
  ['Post events ($0.99 free / $9.99 paid)', true, true],
  ['Search nearby play locations', true, true],
  ['Read & post on the Discussion Board', true, true],
  ['Search & connect with other players', false, true],
  ['In-site messaging', false, true],
  ['Email & optional SMS alerts', false, true],
  ['Request-a-game with 1–3 players', false, true],
];

const INSTRUCTOR_TIER_ROWS = [
  ['Instructor profile & directory listing', true, true, true],
  ['View events & post events', true, true, true],
  ['Search nearby play locations', true, true, true],
  ['Read & post on the Discussion Board', true, true, true],
  ['Player search, messaging, alerts', false, true, true],
  ['Request-a-game with 1–3 players', false, true, true],
  ['Free-to-attend events: free to post', false, true, true],
  ['Paid events: discounted ($6.99)', false, true, '—'],
  ['Paid events: unlimited & free', false, false, true],
  ['Premium Instructor badge', false, false, true],
  ['Priority placement in instructor directory', false, false, true],
];

function Cell({ value }) {
  if (value === true) return <Check className="w-4 h-4 text-[#56dfb4] mx-auto" />;
  if (value === false) return <Minus className="w-4 h-4 text-slate-300 mx-auto" />;
  return <span className="text-xs text-slate-400">{value}</span>;
}

const sections = [
  {
    title: 'Getting Started',
    color: '#56dfb4',
    faqs: [
      { q: 'What is American Mahjong?', a: 'American Mahjong (also spelled Mah Jongg) is a tile-based game adapted from the Chinese game. It uses a set of 152 tiles and follows a card published annually by the National Mah Jongg League (NMJL). It\'s typically played with four players.' },
      { q: 'Do I need to be an experienced player?', a: 'Not at all! Meet For Mahjong welcomes players of all skill levels, from brand new to advanced. You can filter players by skill level to find the right group.' },
      { q: 'Is it free to join?', a: 'Yes! Creating an account and setting up your profile is completely free on our Open Play (player) and Instructor Starter (instructor) tiers. To search for and message other players, post Last-Minute Game Requests, or get the Premium Instructor badge, you\'ll need a paid plan.' },
      { q: 'Do I need a Mahjong set?', a: 'No, you don\'t need to have your own set. During registration, you can indicate whether you have a set, and you can filter players who have sets available.' },
    ],
  },
  {
    title: 'Finding Players & Meetups',
    color: '#56c6df',
    faqs: [
      { q: 'How does finding players work?', a: 'We connect players based on location (zip code), skill level, availability, meetup preferences, and travel distance. You can use our search filters to find compatible players near you. Player search and in-app messaging are available on Verified Player and Instructor Advantage / Instructor Unlimited plans.' },
      { q: 'Are meetups in public or private locations?', a: 'You choose your preference during registration. You can select public meetups only (coffee shops, libraries, community centers) or both public and private locations (including homes). For safety, we recommend your first meetup with any new player always be in a public place.' },
      { q: 'What if I move to a new area?', a: 'Simply update your zip code in your profile, and you\'ll be connected with players in your new area.' },
      { q: 'Can I play with more than 4 people?', a: 'Standard American Mahjong is a 4-player game. If you already have 1–3 players locked in and need to fill the table, use the Last-Minute Game Request feature on the Find Players page — we\'ll alert nearby opted-in players and they can RSVP straight from the email. See the "Last-Minute Game Requests" section below for details.' },
    ],
  },
  {
    title: 'Events',
    color: '#df5681',
    faqs: [
      { q: 'How do I post an event?', a: 'Navigate to the Events page and click "Post an Event." Fill out the event details including type, date, location, and skill levels. Posting fees depend on your plan and whether the event is free or paid for attendees — see "How much does it cost to post an event?" below for the full breakdown.' },
      { q: 'How much does it cost to post an event?', a: 'Free-to-attend events: Open Play, Verified Player, and Instructor Starter pay $0.99 per post; Instructor Advantage and Instructor Unlimited post free-to-attend events for free. Paid-to-attend events: Open Play, Verified Player, and Instructor Starter pay $9.99 per post; Instructor Advantage pays a discounted $6.99 per post; Instructor Unlimited posts paid events for free, with no cap. The exact fee is always shown on the Post Event page before you check out.' },
      { q: 'What types of events can I post?', a: 'You can post Teaching Sessions, Tournaments, Casual Play meetups, and Special Events. Each type is color-coded for easy browsing.' },
      { q: 'Can I edit or cancel my event?', a: 'Yes — both. From your Dashboard or the "My Events" page, you can edit the time, venue, details, and pricing of any event you\'ve posted, right up until it starts. You can also cancel from those same pages. If anyone has saved or RSVP\'d to the event, they\'ll be notified of any changes or cancellations.' },
      { q: 'Do I get a reminder before my event?', a: 'Yes. Hosts automatically receive a friendly nudge 7 days before each event with a one-click link to edit time, venue, or details if anything has changed. If you\'ve opted into SMS alerts, you\'ll also get a text.' },
    ],
  },
  {
    title: 'Last-Minute Game Requests',
    color: '#df5681',
    faqs: [
      { q: 'What is a Last-Minute Game Request?', a: 'It\'s a one-tap way to fill an empty seat at your Mahjong table fast. Available on Verified Player, Instructor Advantage, and Instructor Unlimited plans. From the Find Players page, click "Post Game Request," choose how many players you need (1–3), pick a date + time, optionally add a venue or message, and post. We\'ll instantly email every opted-in player within 25 miles with a one-tap RSVP link.' },
      { q: 'How do players RSVP?', a: 'Recipients see a "Yes, I\'m in" button in the broadcast email. Tapping it confirms their seat without requiring them to log in — RSVPs are authenticated via a secure, signed magic link. First-come, first-served: the first 1–3 to tap (depending on how many seats you opened) are confirmed; anyone after lands on a waitlist.' },
      { q: 'What if a confirmed player drops out?', a: 'When someone cancels their RSVP or you remove them from the roster, the next person on the waitlist is automatically promoted to confirmed and notified by email — no manual juggling needed.' },
      { q: 'How long does a request stay open?', a: 'The request runs until 30 minutes after your scheduled game time, then auto-expires. You can also cancel a request at any time from the request page — everyone on the confirmed list and waitlist will be emailed.' },
      { q: 'Will I see when a game request is posted near me?', a: 'Yes. If you\'re on a paid player plan (Verified Player, Instructor Advantage, or Instructor Unlimited) and have your zip code set, you\'ll see open game requests within 25 miles right on your Dashboard, plus a small red dot on your profile menu so you\'re never far from one tap. You\'ll also receive an email (and SMS, if opted in).' },
      { q: 'How do I stop receiving last-minute alerts?', a: 'Go to Settings → New-event & Last-Minute Alerts and toggle them off. You can also turn off SMS independently or change your max travel distance.' },
    ],
  },
  {
    title: 'Instructors & Learning',
    color: '#b456df',
    faqs: [
      { q: 'How do I find a Mahjong instructor?', a: 'Visit the Instructors page from the navigation menu. You\'ll find a directory of registered instructors with their profiles, skill specialties, and contact options for group or private lessons.' },
      { q: 'How do I become a listed instructor?', a: 'During registration (or by editing your profile), indicate that you teach Mahjong. You\'ll have the option to create a public instructor profile visible to all registered users, including your teaching style, rates, and availability.' },
    ],
  },
  {
    title: 'Forum & Community',
    color: '#56dfb4',
    faqs: [
      { q: 'How does the Discussion Board work?', a: 'The Discussion Board has categories for Tiles, Accessories, NMJL Card Questions, Deals & Coupons, and more. Paid members can create threads and post replies. You can also buy, sell, and trade Mahjong items in the Marketplace section.' },
      { q: 'What are the forum guidelines?', a: 'Be respectful and constructive. No harassment, spam, or sharing personal data. Provide sources or examples when possible. Admins can pin, lock, or remove threads that violate guidelines.' },
    ],
  },
  {
    title: 'Subscriptions & Payments',
    color: '#56c6df',
    faqs: [
      { q: 'How do I cancel my subscription?', a: 'You can cancel your subscription anytime from your Settings page. If you cancel, you\'ll retain access until the end of your current billing period, then revert to the Open Play (player) or Instructor Starter (instructor) free tier — no data is lost.' },
      { q: 'Can I use promo codes?', a: 'Yes! Enter promo codes on the Subscription page. We periodically run limited-time campaigns — when one is active, the offer banner appears on the homepage and Pricing page. You can also earn free months year-round by referring friends — see below.' },
      { q: 'What is the Refer a Friend program?', a: 'When a friend uses your referral code and upgrades to a Verified Player Membership, they receive one free month and you receive one free month on your Verified Player Membership too. Instructors who refer 10 Verified Players receive one free month of Instructor Unlimited. No limit on referrals!' },
    ],
  },
  {
    title: 'Safety & Community',
    color: '#df5681',
    faqs: [
      { q: 'Is the platform safe?', a: 'We take safety seriously. We have community guidelines, reporting features, and player blocking. Our admin team reviews all reports. You can also link your social media accounts to your profile so other members can recognize you and build trust before meeting up.' },
      { q: 'What safety precautions should I take?', a: 'We strongly recommend that your first meetup with a new player always be in a public place such as a coffee shop, library, or community center. Tell a friend or family member about your plans. Trust your instincts, and report any suspicious behavior through our reporting tools.' },
      { q: 'Can men and women play together?', a: 'Absolutely! American Mahjong is a game for everyone regardless of gender. During registration, you can set a gender preference for your playing groups if you wish, or select "No preference" to be open to playing with all members. Our community is inclusive and welcoming to all.' },
    ],
  },
];

export default function FAQPage() {
  // Flatten all Q&As into a single Schema.org FAQPage blob so Google can
  // render rich Q&A snippets directly in search results.
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: sections.flatMap(s => s.faqs).map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <div data-testid="faq-page" className="py-16 lg:py-24">
      <SEO
        title="FAQ — American Mahjong Questions Answered"
        description="Frequently asked questions about finding Mahjong players, booking events, hiring instructors, joining the discussion board, and using Meet For Mahjong."
        path="/faq"
        jsonLd={faqJsonLd}
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-sm font-bold uppercase tracking-[0.2em] text-[#56dfb4]">FAQ</span>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mt-3 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Frequently Asked Questions
          </h1>
          <p className="text-slate-500 text-lg">Everything you need to know about Meet For Mahjong.</p>
        </div>

        {/* Install as an app — PWA instructions for iPhone + Android.
            Lives at the top because returning visitors looking for this
            tend to expect it to be the very first thing on the FAQ. */}
        <div
          className="mb-12 rounded-2xl border border-[#56c6df]/30 bg-gradient-to-br from-[#56dfb4]/10 via-white to-[#b456df]/10 p-6 sm:p-8"
          data-testid="faq-install-app-panel"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-white shadow-sm border border-slate-100 flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-[#56c6df]" />
            </div>
            <h2 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Use Meet For Mahjong like an app on your phone
            </h2>
          </div>
          <p className="text-sm text-slate-600 mb-5">
            There&apos;s no separate app to download &mdash; you can add this site straight to your home screen and it&apos;ll open like any other app, full-screen, with its own icon. Takes about 10 seconds.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-xl bg-white border border-slate-100 p-4" data-testid="faq-install-iphone">
              <p className="font-semibold text-sm mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                iPhone &amp; iPad (Safari)
              </p>
              <ol className="text-sm text-slate-600 space-y-1.5 list-decimal list-inside leading-relaxed">
                <li>Open <span className="font-medium text-slate-800">meetformahjong.com</span> in Safari.</li>
                <li>Tap the <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700"><Share2 className="w-3 h-3" /> Share</span> button at the bottom (or top) of the screen.</li>
                <li>Scroll and tap <span className="font-medium text-slate-800">&ldquo;Add to Home Screen&rdquo;</span>.</li>
                <li>Tap <span className="font-medium text-slate-800">Add</span> in the top-right corner. Done!</li>
              </ol>
              <p className="text-xs text-slate-400 mt-2">Chrome on iPhone/iPad doesn&apos;t support installs &mdash; please use Safari.</p>
            </div>
            <div className="rounded-xl bg-white border border-slate-100 p-4" data-testid="faq-install-android">
              <p className="font-semibold text-sm mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Android (Chrome)
              </p>
              <ol className="text-sm text-slate-600 space-y-1.5 list-decimal list-inside leading-relaxed">
                <li>Open <span className="font-medium text-slate-800">meetformahjong.com</span> in Chrome.</li>
                <li>Tap the <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700"><MoreVertical className="w-3 h-3" /> menu</span> (three dots, top-right).</li>
                <li>Tap <span className="font-medium text-slate-800">&ldquo;Install app&rdquo;</span> or <span className="font-medium text-slate-800">&ldquo;Add to Home screen&rdquo;</span>.</li>
                <li>Confirm <span className="font-medium text-slate-800">Install</span>. The icon appears on your home screen!</li>
              </ol>
              <p className="text-xs text-slate-400 mt-2">Most Android browsers (Samsung Internet, Firefox, Edge) work the same way through their menu.</p>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-4 leading-relaxed">
            Once installed, the app opens in its own window with no browser bar &mdash; just like a native app. You&apos;ll get the same updates as the website automatically. To uninstall, long-press the icon and choose Remove.
          </p>
        </div>


        {/* Membership Tiers — Quick Comparison */}
        <div className="mb-12" data-testid="faq-tier-comparison">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <span className="w-2.5 h-2.5 rounded-full bg-[#56dfb4]" />
            Membership Tiers — Quick Comparison
          </h2>
          <p className="text-sm text-slate-500 mb-4">
            Both free tiers include a lot already. Paid tiers add features on top —{' '}
            <Link to="/about" className="text-[#56dfb4] font-semibold hover:underline">see the full breakdown</Link>.
          </p>

          {/* Players table */}
          <div className="border border-slate-100 rounded-2xl bg-white p-4 mb-4 overflow-x-auto" data-testid="faq-players-table">
            <h3 className="text-sm font-bold text-slate-700 mb-3 px-2" style={{ fontFamily: 'Outfit, sans-serif' }}>For Players</h3>
            <table className="w-full text-sm min-w-[480px]">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left font-semibold text-slate-600 px-2 py-2">Feature</th>
                  <th className="text-center font-semibold text-slate-600 px-2 py-2 w-1/4">
                    Open Play<br />
                    <span className="text-[10px] font-normal text-slate-400">Free</span>
                  </th>
                  <th className="text-center font-semibold text-slate-600 px-2 py-2 w-1/4">
                    Verified Player<br />
                    <span className="text-[10px] font-normal text-slate-400">$0.99/mo · $9.99/yr</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {PLAYER_TIER_ROWS.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? '' : 'bg-slate-50/50'}>
                    <td className="text-left text-slate-700 px-2 py-1.5">{row[0]}</td>
                    <td className="text-center px-2 py-1.5"><Cell value={row[1]} /></td>
                    <td className="text-center px-2 py-1.5"><Cell value={row[2]} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Instructors table */}
          <div className="border border-slate-100 rounded-2xl bg-white p-4 overflow-x-auto" data-testid="faq-instructors-table">
            <h3 className="text-sm font-bold text-slate-700 mb-3 px-2" style={{ fontFamily: 'Outfit, sans-serif' }}>For Instructors</h3>
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left font-semibold text-slate-600 px-2 py-2">Feature</th>
                  <th className="text-center font-semibold text-slate-600 px-2 py-2">
                    Instructor Starter<br />
                    <span className="text-[10px] font-normal text-slate-400">Free</span>
                  </th>
                  <th className="text-center font-semibold text-slate-600 px-2 py-2">
                    Instructor Advantage<br />
                    <span className="text-[10px] font-normal text-slate-400">$9.99/mo · $99.99/yr</span>
                  </th>
                  <th className="text-center font-semibold text-slate-600 px-2 py-2">
                    Instructor Unlimited<br />
                    <span className="text-[10px] font-normal text-slate-400">$29.99/mo · $299.99/yr</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {INSTRUCTOR_TIER_ROWS.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? '' : 'bg-slate-50/50'}>
                    <td className="text-left text-slate-700 px-2 py-1.5">{row[0]}</td>
                    <td className="text-center px-2 py-1.5"><Cell value={row[1]} /></td>
                    <td className="text-center px-2 py-1.5"><Cell value={row[2]} /></td>
                    <td className="text-center px-2 py-1.5"><Cell value={row[3]} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>



        <div className="space-y-10">
          {sections.map((section, si) => (
            <div key={si}>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: section.color }} />
                {section.title}
              </h2>
              <Accordion type="single" collapsible className="space-y-3">
                {section.faqs.map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${si}-${i}`} className="border border-slate-100 rounded-2xl px-6 bg-white data-[state=open]:shadow-sm transition-all" data-testid={`faq-item-${si}-${i}`}>
                    <AccordionTrigger className="text-left py-5 hover:no-underline font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-500 pb-5 leading-relaxed">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
